/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { ITopicExplanation } from '../services/topicService';
import { submitChallenge } from '../services/challengeService';

interface ChallengeExplanationModalProps {
  open: boolean;
  onClose: () => void;
  explanation: ITopicExplanation | null;
  topicId: string;
  explanationIndex: number;
  topicName: string;
  userPoints?: number;
  canChallengeForFree?: boolean;
}

const ChallengeExplanationModal: React.FC<ChallengeExplanationModalProps> = ({
  open,
  onClose,
  explanation,
  topicId,
  explanationIndex,
  topicName,
  userPoints = 0,
  canChallengeForFree = false
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [challengeText, setChallengeText] = useState('');
  const [challengeType, setChallengeType] = useState<'refutation' | 'replacement' | 'evidence_contradiction'>('refutation');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const challengeCost = 100; // This should match config.cube.challengeCost

  const handleSubmit = async () => {
    if (!challengeText.trim()) {
      setError('Challenge text is required');
      return;
    }

    if (challengeText.length < 50) {
      setError('Challenge must be at least 50 characters long');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await submitChallenge({
        topicId,
        explanationIndex,
        challengeText: challengeText.trim(),
        challengeType
      });

      // If we got a debate ID, navigate directly to the debate
      if (result.debateId) {
        // Close modal and navigate to debate
        onClose();
        setChallengeText('');
        setChallengeType('refutation');
        setSuccess(false);
        setIsSubmitting(false);
        
        // Navigate to the CUBE challenge debate
        navigate(`/debate/${result.debateId}`);
      } else {
        // Show success message and keep it persistent
        setSuccess(true);
        // Don't auto-close - let the user close it manually or navigate away
      }

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit challenge');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setChallengeText('');
      setChallengeType('refutation');
      setError(null);
      setSuccess(false);
    }
  };

  const getChallengeTypeDescription = (type: string) => {
    switch (type) {
      case 'refutation':
        return 'Point out logical flaws, contradictions, or errors in reasoning';
      case 'replacement':
        return 'Provide a better alternative explanation for the same phenomenon';
      case 'evidence_contradiction':
        return 'Present evidence that contradicts the explanation';
      default:
        return '';
    }
  };

  const getPaymentInfo = () => {
    if (canChallengeForFree) {
      return {
        cost: 0,
        message: 'Free (you are a contributor or successful challenger)',
        color: 'success' as const
      };
    } else if (userPoints >= challengeCost) {
      return {
        cost: challengeCost,
        message: `Costs ${challengeCost} CUBE points`,
        color: 'info' as const
      };
    } else {
      return {
        cost: challengeCost,
        message: `Insufficient points (need ${challengeCost}, have ${userPoints})`,
        color: 'error' as const
      };
    }
  };

  const paymentInfo = getPaymentInfo();
  const canAffordChallenge = canChallengeForFree || userPoints >= challengeCost;

  if (!explanation) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        Challenge Explanation
        <Box component="div" sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
          Topic: {topicName}
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('cubeSubmissionSuccessTitle')}
              </Typography>
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('cubeSubmissionSuccessDescription')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('cubeSubmissionSuccessWaitingText')} <strong>{t('cubeSubmissionSuccessDebatesLink')}</strong> {t('cubeSubmissionSuccessLinkText')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                window.location.href = '/home';
              }}
              sx={{ mt: 2 }}
            >
              {t('cubeSubmissionSuccessDebatesLink')}
            </Button>
          </Box>
        ) : (
          <>
            {/* Current Explanation Display */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Explanation:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{explanation.text.substring(0, 300)}{explanation.text.length > 300 ? '...' : ''}"
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  size="small" 
                  label={`Status: ${explanation.status || 'active'}`}
                  color={explanation.status === 'active' ? 'success' : explanation.status === 'challenged' ? 'error' : 'warning'}
                />
                {explanation.challengesSurvived !== undefined && (
                  <Chip 
                    size="small" 
                    label={`Challenges Survived: ${explanation.challengesSurvived}`}
                    variant="outlined"
                  />
                )}
                {explanation.version && explanation.version > 1 && (
                  <Chip 
                    size="small" 
                    label={`Version: ${explanation.version}`}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            {/* Challenge Type Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Challenge Type</InputLabel>
              <Select
                value={challengeType}
                onChange={(e) => setChallengeType(e.target.value as any)}
                label="Challenge Type"
              >
                <MenuItem value="refutation">Refutation</MenuItem>
                <MenuItem value="replacement">Replacement</MenuItem>
                <MenuItem value="evidence_contradiction">Evidence Contradiction</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {getChallengeTypeDescription(challengeType)}
              </Typography>
            </FormControl>

            {/* Challenge Text Input */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Your Challenge"
              placeholder={`Explain why you believe this explanation ${
                challengeType === 'refutation' ? 'contains logical flaws or errors' :
                challengeType === 'replacement' ? 'can be improved with a better alternative' :
                'contradicts available evidence'
              }. Be specific and provide clear reasoning...`}
              value={challengeText}
              onChange={(e) => setChallengeText(e.target.value)}
              sx={{ mb: 2 }}
              helperText={`${challengeText.length}/1000 characters (minimum 50)`}
              inputProps={{ maxLength: 1000 }}
            />

            {/* Payment Information */}
            <Alert severity={paymentInfo.color} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Cost:</strong> {paymentInfo.message}
                {!canChallengeForFree && (
                  <>
                    <br />
                    <small>
                      Points will be refunded if your challenge succeeds. 
                      Failed challenges forfeit points to discourage weak attempts.
                      Contributors and successful challengers can challenge for free.
                    </small>
                  </>
                )}
              </Typography>
            </Alert>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {success ? (
          <Button 
            onClick={handleClose} 
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting || !canAffordChallenge || challengeText.length < 50}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Submitting...' : `Submit Challenge${paymentInfo.cost > 0 ? ` (${paymentInfo.cost} points)` : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChallengeExplanationModal; 